import { ArrowLeftOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Card, Col, Row, Spin, Statistic, Tabs, Tooltip } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
    fetchGatheringById,
    fetchGatheringEvents,
    fetchGatheringParticipants,
    fetchGatheringStats,
} from '../api/fetchGatherings.js';
import ProfileImage from '../components/ProfileImage.js';
import { showToast } from '../utils/toastUtils.js';

function GroupDetails() {
    const { gathering_id } = useParams();
    const navigate = useNavigate();

    const [gathering, setGathering] = useState(null);
    const [events, setEvents] = useState(null);
    const [participants, setParticipants] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    // Fetch all data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [gatheringData, eventsData, participantsData, statsData] = await Promise.all([
                fetchGatheringById(gathering_id),
                fetchGatheringEvents(gathering_id),
                fetchGatheringParticipants(gathering_id),
                fetchGatheringStats(gathering_id),
            ]);

            setGathering(gatheringData);
            setEvents(eventsData);
            setParticipants(participantsData);
            setStats(statsData);
        } catch (err) {
            console.error('Error fetching group details:', err);
            showToast('Failed to load group details', 'error');
        } finally {
            setLoading(false);
        }
    }, [gathering_id]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleBack = () => {
        navigate('/admin/groups');
    };

    const handleEventClick = (eventId) => {
        navigate(`/admin/events/${eventId}`);
    };



    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '60px' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (!gathering) {
        return (
            <div style={{ textAlign: 'center', padding: '60px' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px', color: '#6b7280' }}>
                    Group not found
                </div>
                <button
                    type="button"
                    className="button is-primary"
                    onClick={handleBack}
                    style={{ marginTop: '20px' }}
                >
                    Back to Groups
                </button>
            </div>
        );
    }

    return (
        <>
            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <button
                    type="button"
                    className="button is-light"
                    onClick={handleBack}
                    style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <ArrowLeftOutlined />
                    Back to Groups
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                    {gathering.image_url ? (
                        <img
                            src={gathering.image_url}
                            alt={gathering.name}
                            style={{ width: '80px', height: '80px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }}
                        />
                    ) : (
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '8px',
                            background: 'linear-gradient(135deg, #f0f0f0 0%, #e5e7eb 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '40px',
                            border: '1px solid #e5e7eb'
                        }}>
                            📁
                        </div>
                    )}
                    <div>
                        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '600', color: '#1f2937' }}>{gathering.name}</h1>
                        <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '14px' }}>
                            Created {new Date(gathering.createdAt).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                {/* Info box about Groups */}
                <div style={{
                    backgroundColor: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'start',
                    gap: '12px'
                }}>
                    <InfoCircleOutlined style={{ color: '#3b82f6', fontSize: '16px', marginTop: '2px' }} />
                    <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '13px', color: '#1e40af', lineHeight: '1.5' }}>
                            <strong>About Groups:</strong> Groups are organizational containers that help you view which participants have access to which events.
                            Participants are assigned to individual events, not directly to groups. To add or remove participants, manage them through the individual event pages.
                        </p>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <Row gutter={16} style={{ marginBottom: '24px' }}>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Events"
                                value={stats.total_events}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Active Events"
                                value={stats.active_events}
                                valueStyle={{ color: '#52c41a' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Participants"
                                value={stats.total_participants}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Upcoming Sessions"
                                value={stats.upcoming_sessions}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* Tabs for Events and Participants */}
            <Tabs
                defaultActiveKey="events"
                items={[
                    {
                        key: 'events',
                        label: `Events (${events?.length || 0})`,
                        children: (
                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}>
                                {events && events.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{
                                                backgroundColor: '#f9fafb',
                                                borderBottom: '2px solid #e5e7eb'
                                            }}>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Event Name</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Venue</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Location</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '100px'
                                                }}>Status</th>
                                                <th style={{
                                                    padding: '8px 12px',
                                                    textAlign: 'right',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '100px'
                                                }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {events.map((event) => (
                                                <tr
                                                    key={event.event_id}
                                                    style={{
                                                        borderBottom: '1px solid #e5e7eb',
                                                        cursor: 'pointer'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                                >
                                                    <td style={{ padding: '12px' }}>
                                                        <span style={{ fontWeight: '500', color: '#1f2937' }}>
                                                            {event.name}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px', color: '#6b7280' }}>
                                                        {event.venue?.name || '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', color: '#6b7280' }}>
                                                        {event.venue?.location || '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            padding: '4px 10px',
                                                            borderRadius: '12px',
                                                            background: event.is_paused ? '#fef3c7' : '#d1fae5',
                                                            color: event.is_paused ? '#92400e' : '#065f46',
                                                            fontWeight: '600'
                                                        }}>
                                                            {event.is_paused ? 'Paused' : 'Active'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                                                        <button
                                                            type="button"
                                                            className="button is-light"
                                                            onClick={() => handleEventClick(event.event_id)}
                                                            style={{
                                                                fontSize: '11px',
                                                                fontWeight: '500',
                                                                padding: '4px 10px',
                                                                height: 'auto'
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{
                                        padding: '60px 20px',
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📅</div>
                                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            No events in this group yet
                                        </div>
                                        <div style={{ fontSize: '14px' }}>
                                            Events assigned to this group will appear here
                                        </div>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        key: 'participants',
                        label: `Participants (${participants?.length || 0})`,
                        children: (
                            <div style={{
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}>
                                {participants && participants.length > 0 ? (
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{
                                                backgroundColor: '#f9fafb',
                                                borderBottom: '2px solid #e5e7eb'
                                            }}>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '40px'
                                                }}></th>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'left',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em'
                                                }}>Participant</th>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '100px'
                                                }}>Events</th>
                                                <th style={{
                                                    padding: '3px 8px',
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: '600',
                                                    color: '#6b7280',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    width: '70px'
                                                }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {participants.map((participant) => (
                                                <tr
                                                    key={participant.participant_id}
                                                    style={{
                                                        borderBottom: '1px solid #e8e8e8',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    <td style={{ padding: '3px 8px', width: '40px', verticalAlign: 'middle' }}>
                                                        <div style={{ width: '26px', height: '26px' }}>
                                                            <ProfileImage participant={participant} size={26} />
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '3px 8px', verticalAlign: 'middle' }}>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                            <span style={{
                                                                fontWeight: '500',
                                                                color: '#1f2937',
                                                                fontSize: '12px'
                                                            }}>
                                                                {participant.name}
                                                            </span>
                                                            {participant.nickname && (
                                                                <span style={{
                                                                    color: '#6b7280',
                                                                    fontSize: '11px'
                                                                }}>
                                                                    "{participant.nickname}"
                                                                </span>
                                                            )}
                                                            {participant.email && (
                                                                <span style={{
                                                                    color: '#9ca3af',
                                                                    fontSize: '10px'
                                                                }}>
                                                                    {participant.email}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '3px 8px', width: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        <span style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            padding: '1px 6px',
                                                            borderRadius: '8px',
                                                            backgroundColor: '#dbeafe',
                                                            color: '#1e40af',
                                                            fontSize: '10px',
                                                            fontWeight: '500'
                                                        }}>
                                                            {participant.event_count} {participant.event_count === 1 ? 'event' : 'events'}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '3px 8px', width: '70px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        {participant.active ? (
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                padding: '1px 6px',
                                                                borderRadius: '8px',
                                                                backgroundColor: '#d1fae5',
                                                                color: '#065f46',
                                                                fontSize: '10px',
                                                                fontWeight: '500'
                                                            }}>
                                                                Active
                                                            </span>
                                                        ) : (
                                                            <span style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                padding: '1px 6px',
                                                                borderRadius: '8px',
                                                                backgroundColor: '#fee2e2',
                                                                color: '#991b1b',
                                                                fontSize: '10px',
                                                                fontWeight: '500'
                                                            }}>
                                                                Inactive
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                ) : (
                                    <div style={{
                                        padding: '60px 20px',
                                        textAlign: 'center',
                                        color: '#6b7280'
                                    }}>
                                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                                        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                                            No participants in this group yet
                                        </div>
                                        <div style={{ fontSize: '14px' }}>
                                            Participants are added through events in this group
                                        </div>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                ]}
            />
        </>
    );
}

export default GroupDetails;

